import { getCurrentInstance, onActivated, onUnmounted, type ComponentInternalInstance } from 'vue';
import type { ZodTypeAny } from 'zod';
import type
{
    AgentToolsHandler,
    AgentToolsInternal,
    AgentToolsRegistrationOptions,
    AgentToolsToolRegistrationOptions
} from '#layers/core/app/runtime/types/tools';
import { agentToolAreasOf } from '#layers/core/app/runtime/utils/agentToolsRegistry';

// Kontrakt rejestru moduł dostaje podany z zewnątrz (plugins/tools.ts) i trzyma w referencji
// modułowej, zamiast czytać go w funkcjach rejestrujących przez useNuxtApp(). Rejestracja bywa
// wołana z onMounted komponentu, z setupu innego pluginu i z testów jednostkowych chodzących bez
// Nuxta (projekt `unit`, environment `node`) - w dwóch ostatnich przypadkach useNuxtApp() nie ma
// czego zwrócić, więc odczyt kontraktu nie może od niego zależeć
//
// Referencja modułowa jest tu bezpieczna, bo panel jest aplikacją jednostronicową (`ssr: false`
// w layers/core/nuxt.config.ts) - moduł żyje w jednej karcie jednego użytkownika i nie ma żądań,
// między którymi ten stan mógłby przeciekać. W projekcie renderowanym po stronie serwera ten sam
// wzorzec oddawałby rejestr jednego użytkownika żądaniu innego - nie przenoś go tam
let contract: AgentToolsInternal | null = null;

export function setAgentToolsRegistry(tools: AgentToolsInternal): void
{
    contract = tools;
}

// Brak rejestru kończy się wyjątkiem, a nie pominięciem rejestracji: dotychczasowe
// `window.edito?.tools?.` po cichu nic nie rejestrowało, więc widok wyglądał na sprawny, choć agent
// nie miał ani jednego narzędzia - a taka pomyłka jest w kolejności ładowania pluginów, nie
// w danych, i musi się zgłosić od razu
function registry(): AgentToolsInternal
{
    if (contract === null)
    {
        throw new Error('Rejestr narzędzi agenta nie jest gotowy - rejestracja musi nastąpić po pluginie `tools`.');
    }

    return contract;
}

// Rejestracje wykonane wewnątrz komponentu, zapamiętane po to, żeby dało się je powtórzyć przy
// ponownej aktywacji widoku. Powód: trasa z `keepalive: true` (kreator stron CMS, kategorie sklepu)
// nie odmontowuje komponentu przy opuszczeniu, więc `onMounted` z rejestracją już się nie powtórzy -
// a poziom ulotny czyści się przy zmianie trasy (`plugins/tools.ts`). Widok wracał więc bez ani
// jednego narzędzia i bez promptu
//
// Powtórzenie stoi tutaj, a nie w każdym mechanizmie z osobna (formularz, lista, sortowanie, przyciski
// paska akcji, prompt widoku): wszystkie rejestrują się tą jedną funkcją, więc jedno miejsce zamyka
// lukę dla wszystkich naraz i nie wymaga ich przebudowy pod zaczep aktywacji
interface ComponentRegistration
{
    instance: ComponentInternalInstance;
    name: string;
    owner?: object;
    replay: () => void;
}

let componentRegistrations: ComponentRegistration[] = [];

// Podczas powtarzania rejestracje nie zapamiętują się ponownie - inaczej każda aktywacja dokładałaby
// do spisu kopię tego samego wpisu
let replaying = false;

// Komponent-właściciel podany z zewnątrz, na czas jednej rejestracji. Powód: formularz w oknie
// modalnym rejestruje się z obserwatora stanu okna, a Vue nie ustawia wtedy bieżącej instancji, więc
// `getCurrentInstance()` oddaje null i okno otwarte po zamontowaniu widoku nie było zapamiętywane -
// czyli nie wracało po powrocie na trasę. Mechanizmy i tak trzymają swoją instancję z setupu, więc
// wystarczy, żeby umiały ją tutaj podać
let owningInstance: ComponentInternalInstance | null = null;

// Nazwa z przedrostkiem, bo `runtime/utils` jest katalogiem auto-importów Nuxta i każdy eksport stąd
// wchodzi do globalnej przestrzeni całego panelu
export function withAgentToolsOwner<T>(instance: ComponentInternalInstance | null, register: () => T): T
{
    const previous = owningInstance;

    owningInstance = instance;

    try
    {
        return register();
    }
    finally
    {
        owningInstance = previous;
    }
}

// Komponenty, na których zaczepy już stoją. Pytamy o zaczepy, a nie o spis wpisów: wpisy znikają przy
// zamknięciu okna, więc oparty o nie warunek zakładałby haki na nowo przy każdym cyklu otwórz/zamknij
const hookedInstances = new WeakSet<ComponentInternalInstance>();

// Zapamiętywane są wyłącznie wpisy ulotne: poziom globalny rejestruje plugin, raz na życie aplikacji,
// i nikt go nie czyści, więc nie ma czego odtwarzać
function remember(name: string, options: AgentToolsRegistrationOptions, replay: () => void): void
{
    // Właściciel podany z zewnątrz ma pierwszeństwo: zna prawdziwego właściciela rejestracji także
    // tam, gdzie Vue nie ma już bieżącej instancji
    const instance = owningInstance ?? getCurrentInstance();

    if (replaying || options.level !== 'ephemeral' || instance === null)
    {
        return;
    }

    // Zaczepy zakładane raz na komponent, przy jego pierwszej rejestracji. `onActivated` na
    // komponencie spoza `<KeepAlive>` nigdy się nie odpala, więc widok bez trzymania w pamięci nie
    // zmienia zachowania ani o krok
    if (!hookedInstances.has(instance))
    {
        hookedInstances.add(instance);

        onActivated(() => replayFor(instance), instance);
        onUnmounted(() => forgetFor(instance), instance);
    }

    componentRegistrations.push({ instance, name, owner: options.owner, replay });
}

// Zdjęcie przed powtórzeniem, bo aktywacja odpala się także zaraz po pierwszym zamontowaniu, kiedy
// wpisy stoją jeszcze w rejestrze - bez tego powtórzenie uderzałoby w kolizję nazwy. Zdejmowane
// z właścicielem, więc powtórzenie nie rusza wpisu cudzego widoku o tej samej nazwie
function replayFor(instance: ComponentInternalInstance): void
{
    const entries = componentRegistrations.filter(entry => entry.instance === instance);

    replaying = true;

    try
    {
        for (const entry of entries)
        {
            registry().unregister(entry.name, entry.owner);
            entry.replay();
        }
    }
    finally
    {
        replaying = false;
    }
}

function forgetFor(instance: ComponentInternalInstance): void
{
    componentRegistrations = componentRegistrations.filter(entry => entry.instance !== instance);
}

export function registerTool(name: string, description: string, schema: ZodTypeAny, handler: AgentToolsHandler, options: AgentToolsToolRegistrationOptions): void
{
    registry().registerTool(name, description, schema, handler, options);
    remember(name, options, () => registry().registerTool(name, description, schema, handler, options));
}

export function registerPrompt(name: string, description: string, handler: AgentToolsHandler, options: AgentToolsRegistrationOptions): void
{
    registry().registerPrompt(name, description, handler, options);
    remember(name, options, () => registry().registerPrompt(name, description, handler, options));
}

export function registerResource(name: string, description: string, handler: AgentToolsHandler, options: AgentToolsRegistrationOptions): void
{
    registry().registerResource(name, description, handler, options);
    remember(name, options, () => registry().registerResource(name, description, handler, options));
}

// Nazwa z przedrostkiem, bo `runtime/utils` jest katalogiem auto-importów Nuxta (layers/core/nuxt.config.ts)
// i każdy eksport stąd wchodzi do globalnej przestrzeni całego panelu - samo `unregister` zderzałoby
// się w czytaniu z lokalnymi funkcjami o tej nazwie w komponentach (np. filtry, pasek edytora)
//
// Brak gotowego rejestru kończy się wyjątkiem, tak samo jak przy rejestracji: pomyłka w kolejności
// ładowania pluginów jest błędem w kodzie i ma się zgłosić od razu. Cicha zgoda dotyczy wyłącznie
// nazwy, której nie ma w gotowym rejestrze - to rozstrzyga sam rejestr
export function unregisterAgentTool(name: string, owner?: object): void
{
    registry().unregister(name, owner);

    // Wpis zdjęty świadomie nie wraca przy ponownej aktywacji widoku - inaczej formularz okna
    // zamkniętego przed opuszczeniem trasy stanąłby po powrocie z powrotem w spisie, choć okna nie ma
    componentRegistrations = componentRegistrations.filter(entry => entry.name !== name || (owner !== undefined && entry.owner !== owner));
}

// Obszary ogłoszone w tej chwili w danej przestrzeni - stan z chwili wywołania, nie z chwili wejścia
// na widok. Zakładka albo okno montowane warunkowo rejestruje swoje wpisy dopiero przy pokazaniu
// i zdejmuje je przy ukryciu, więc lista policzona raz przy rejestracji kłamałaby zaraz po pierwszym
// przełączeniu zakładki
//
// Czytane ze spisu rejestru, bo to on jest jedynym miejscem, w którym obszary faktycznie istnieją.
// Spis milczy bez zalogowanej sesji - i to jest właściwe: bez niej żadnego wpisu i tak nie da się
// wywołać, więc nie ma obszarów, o których byłoby co powiedzieć
export function agentToolAreas(namespace: string): string[]
{
    const listed = registry().list();

    return agentToolAreasOf([...listed.resources, ...listed.tools, ...listed.prompts].map(entry => entry.name), namespace);
}
